import {
  Component,
  computed,
  HostBinding,
  inject,
  OnDestroy,
  Signal,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { MenuItem } from '@components/abb-actions-menu/abb-actions-menu.component';
import { UserModalComponent } from './user-modal/user-modal.component';
import {
  DataTableFilter,
  IColumn,
} from '@components/abb-table/abb-table.component';
import { ImageValueCellComponent } from '@components/abb-table/abb-cells/imageValueCell';
import { ROLES, ROLES_SORTING_ORDER } from '@constants/user';
import { AbbCommonModule } from 'app/abb-common.module';
import { OverlayService } from '@services/ui/overlay.service';
import { Apollo } from 'apollo-angular';
import {
  DeleteUsersMutation,
  DeleteUsersMutationVariables,
  PropertyListQuery,
  UserListQuery,
  Exact,
  Scalars,
  CanDeleteUserQuery,
  CanDeleteUserQueryVariables,
} from 'gql/graphql';
import { BehaviorSubject, combineLatest, first, Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { deleteUsersMutation, userListQuery, canDeleteUserQuery } from '@graphql/user';
import { TRANSLATION_PREFIX } from '@pipes/t.pipe';
import { TranslateService } from '@ngx-translate/core';
import { DataProvider } from '@components/abb-table/data-source';
import { dataProviderFromObservable } from '@utils/data-provider';
import { ToastService } from '@services/ui/toast.service';
import { PAGE, PAGES } from '@constants/constants';
import { CognitoService, UserWithID } from '@services/cognito/cognito.service';
import { LOADING } from '@utils/loader';
import { propertyListQuery } from '@graphql/property';
import { OverflowTooltipDirective } from '@directives/overflow-tooltip.directive';
import { getErrorMessage } from '@utils/errors';
import { t } from '@utils/strings';
import { PermissionsService } from '@services/user/permissions.service';

type UserDataTableFilters = {
  name: string;
  email: string;
  role: string;
};

export interface Permission {
  viewer: boolean;
  editing: boolean;
}
export interface UserDataTable {
  id: string;
  name: string;
  lastName: string;
  imgUrl?: string;
  email: string;
  role: ROLES;
  phone?: string;
  propertyIds: string[];
  properties?: PropertyListQuery['propertyList'];
  commissionerEndDate?: Date | null;
  commissionerPermissions?: Record<PAGE['title'], Permission>;
}

@Component({
  selector: 'abb-users',
  standalone: true,
  template: `
    <abb-table
      [columns]="columns()"
      [dataProvider]="dataProvider"
      [loading]="loading()"
      [actions]="actions"
      [defaultFilters]="defaultFilters"
      [filters]="filters"
      [defaultSortBy]="[
        { column: 'role', order: 'asc' },
        { column: 'name', order: 'asc' }
      ]"
    ></abb-table>
    <ng-template #name let-item>
      <abb-image-value-cell
        [value]="item.name + ' ' + item.lastName"
        [image]="item.imgUrl"
      />
    </ng-template>
    <ng-template #properties let-item>
      <span overflowTooltip class="block truncate">
        @for (p of item.properties; track p) {
        {{ p.name }}@if (!$last) {, } }
      </span>
    </ng-template>
  `,
  styles: ``,
  imports: [
    CommonModule,
    AbbCommonModule,
    ImageValueCellComponent,
    OverflowTooltipDirective,
  ],
  providers: [{ provide: TRANSLATION_PREFIX, useValue: 'settings.tabs.users' }],
})
export class UsersComponent implements OnDestroy {
  @HostBinding('attr.id') componentid = 'users-component';
  nameTemplate = viewChild<TemplateRef<unknown>>('name');
  propertiesTemplate = viewChild<TemplateRef<unknown>>('properties');
  properties$ = new BehaviorSubject<PropertyListQuery['propertyList']>([]);
  canEdit = computed(() => this.permissionsService.canEditPage(PAGES.SETTINGS))
  columns = computed<IColumn<UserDataTable>[]>(() => {
    return [
      {
        id: 'id',
        name: 'Id',
        key: 'id',
        hidden: true,
      },
      {
        id: 'name',
        name: 'Name',
        templateRef: this.nameTemplate(),
        translate: true,
      },
      {
        id: 'email',
        name: 'Email',
        key: 'email',
        translate: true,
      },
      {
        id: 'role',
        name: 'Role',
        key: 'role',
        translate: true,
      },
      {
        id: 'properties',
        name: 'Properties',
        templateRef: this.propertiesTemplate(),
        translate: true,
      },
      {
        name: 'actions',
        tableCellClass: this.canEdit() ? 'w-0' : 'hidden',
        tableHeaderCellClass: this.canEdit() ? 'w-0' :  'hidden'
      },
    ];
  });
  users$ = new BehaviorSubject<UserDataTable[] | []>([]);
  filters: DataTableFilter<UserDataTableFilters>[] = [
    { id: 'name', filterType: 'text', label: 'Name', className: 'w-full' },
    { id: 'email', filterType: 'text', label: 'Email', className: 'w-full' },
    {
      id: 'role',
      filterType: 'select',
      label: 'Role',
      // TODO: cambiare, i ruoli probabilmente dovrebbero venire dal server
      // e il confronto dovrebbe essere fatto sull'id e non sul campo testuale
      filterOptions: Object.entries(ROLES).map(([key, value]) => {
        return { value, label: value };
      }),
      className: 'w-full',
    },
  ];
  defaultFilters: UserDataTableFilters = {
    email: '',
    name: '',
    role: '',
  };
  dataProvider: DataProvider<UserDataTable, UserDataTableFilters> =
    dataProviderFromObservable(
      this.users$,
      (el, filter) => {
        return (
          el.email.toLowerCase().includes(filter.email.toLowerCase()) &&
          (el.name + ' ' + el.lastName)
            .toLowerCase()
            .includes(filter.name.toLowerCase()) &&
          (!filter.role || el.role === filter.role)
        );
      },
      (a, b, cols) => {
        for (let c of cols) {
          let itemA, itemB;
          if (c.column === 'role') {
            itemA = ROLES_SORTING_ORDER[a.role];
            itemB = ROLES_SORTING_ORDER[b.role];
          } else if (c.column === 'name') {
            itemA = `${a.name} ${a.lastName}`.toLowerCase();
            itemB = `${b.name} ${b.lastName}`.toLowerCase();
          } else {
            const key = c.column as keyof Omit<
              UserDataTable,
              'properties' | 'role' | 'name'
            >;
            itemA =
              a[key] instanceof Date
                ? a[key] ?? ''
                : (a[key] as string)?.toLowerCase();
            itemB =
              b[key] instanceof Date
                ? b[key] ?? ''
                : (b[key] as string)?.toLowerCase();
          }

          if (itemA < itemB) {
            return c.order === 'asc' ? -1 : 1;
          }
          if (itemA > itemB) {
            return c.order === 'asc' ? 1 : -1;
          }
        }

        return 0;
      }
    );
  loading = signal(true);
  private loggedUser: Signal<UserWithID | null>;

  prefix = inject(TRANSLATION_PREFIX);
  private subscriptions: Subscription[] = [];
  constructor(
    private readonly overlayService: OverlayService,
    private readonly apollo: Apollo,
    private readonly translate: TranslateService,
    private readonly toast: ToastService,
    readonly cognitoService: CognitoService,
    private readonly permissionsService: PermissionsService
  ) {
    this.loggedUser = cognitoService.loggedUser;

    this.subscriptions.push(
      combineLatest([
        this.apollo.watchQuery<PropertyListQuery>({
          query: propertyListQuery,
        }).valueChanges,
        this.apollo.watchQuery<UserListQuery>({
          query: userListQuery,
        }).valueChanges,
      ]).subscribe({
        next: ([properties, users]) => {
          const u = users.data.userList.reduce(
            (acc: UserDataTable[], current) => {
              const commissionerPermissions = current.commissionerPermissions
                ? current.commissionerPermissions.reduce((accum, curr) => {
                    return {
                      ...accum,
                      [curr!.key]: {
                        viewer: curr!.value.viewer,
                        editing: curr!.value.editing,
                      },
                    };
                  }, {})
                : undefined;
              return [
                ...acc,
                {
                  ...current,
                  name: current.name,
                  lastName: current.lastName,
                  imgUrl: current.imgUrl ?? undefined,
                  role: ROLES[current.role],
                  phone: current.phone ?? undefined,
                  commissionerEndDate: current.commissionerEndDate ?? null,
                  commissionerPermissions,
                  properties: properties.data.propertyList.filter((p) =>
                    current.propertyIds.includes(p.id)
                  ),
                },
              ];
            },
            []
          );

          this.users$.next(u);
          this.loading.set(false);
        },
        error: (err) => {
          this.loading.set(false);
          //let errMsg = getErrorMessage(err, this.translate);
          //this.toast.notifyError(errMsg);
        },
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  actions: Array<MenuItem<UserDataTable>> = [
    { label: 'Edit User', action: (users) => this.openEditModal(users![0]) },
    {
      label: 'Remove User',
      disabledCondition: (user: UserDataTable) =>
        user.id === this.loggedUser()?.id,
      action: (users) => this.openDeleteModal(users![0]),
    },
  ];

  openEditModal(user: UserDataTable) {
    this.overlayService.openModal(UserModalComponent, { mode: 'edit', user });
  }
  awaitingRes = inject(LOADING);
  openDeleteModal(user: UserDataTable) {
    // Show loading modal while validating
    this.awaitingRes.set(true);
    setTimeout(() => {
      this.apollo
      .query<CanDeleteUserQuery, CanDeleteUserQueryVariables>({
        query: canDeleteUserQuery,
        variables: { userId: user.id },
      })
      .pipe(first())
      .subscribe({
        next: ({ data }) => {
          this.awaitingRes.set(false);
          
          if (!data.canDeleteUser.canDelete) {
            // Show error modal if user cannot be deleted
            this.overlayService.openAlert({
              message: data.canDeleteUser.reason || 
                t(this.prefix, 'This user cannot be deleted', this.translate),
              confirmText: t(this.prefix, 'Okay', this.translate),
              title: t(this.prefix, 'Cannot delete user', this.translate),
            });
            return;
          }
          
          // If user can be deleted, show confirmation modal
          this.overlayService
            .openAlert({
              message: `${t(
                this.prefix,
                'Are you sure you want to remove user',
                this.translate
              )} <b>${user.name!} ${user.lastName!}</b>?`,
              htmlMessage: true,
              confirmText: t(this.prefix, 'Remove', this.translate),
            })
            .then(() => {
              this.awaitingRes.set(true);
              this.deleteUsers([user.id]).finally(() => {
                this.awaitingRes.set(false);
                this.apollo.client.refetchQueries({
                  include: ['UserList']
                });
              });
            })
            .catch(() => console.log('cancel'));
        },
        error: (err) => {
          this.awaitingRes.set(false);
          getErrorMessage(err, this.translate).subscribe(errMsg => {
            this.overlayService.openAlert({
              message: errMsg || 
                t(this.prefix, 'An error occurred while checking user permissions', this.translate),
              confirmText: t(this.prefix, 'Okay', this.translate),
            });
          });
        },
      });
    }, 5000);
    // First check if user can be deleted
    
  }

  deleteUsers(ids: string | string[]) {
    return new Promise<void>((resolve, reject) =>
      this.apollo
        .mutate<DeleteUsersMutation, DeleteUsersMutationVariables>({
          mutation: deleteUsersMutation,
          variables: {
            ids,
          },
          update: (cache, { data }) => {
            cache.writeQuery({
              query: userListQuery,
              data: { userList: data?.deleteUsers },
            });
          },
        })
        .pipe(first())
        .subscribe({
          next: () => {
            resolve();
            this.toast.notifySuccess(
              t(this.prefix, `User deleted successfully`, this.translate)
            );
          },
          error: () => {
            reject();
            this.toast.notifySuccess(
              t(
                this.prefix,
                `An error occurred while deleting the user`,
                this.translate
              )
            );
          },
        })
    );
  }
}
